import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Video, Circle, Pencil, Trash2, Check, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { relTime, formatApiErrorDetail } from "@/lib/constants";
import MessageComposer from "@/components/MessageComposer";
import MessageAttachments from "@/components/MessageAttachments";
import { Input } from "@/components/ui/input";

export default function DirectMessages({ otherId, onBack, onCall }) {
  const { user } = useAuth();
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const atBottomRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/dms/${otherId}/messages`);
      setOther(r.data.user);
      setMessages(r.data.messages);
    } catch (e) { /* ignore */ }
  }, [otherId]);

  useEffect(() => {
    setMessages([]);
    atBottomRef.current = true;
    load();
    const int = setInterval(load, 2500);
    return () => clearInterval(int);
  }, [otherId, load]);

  useEffect(() => {
    if (atBottomRef.current) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const send = async (content, attachments) => {
    try {
      const r = await api.post(`/dms/${otherId}/messages`, { content, attachments });
      setMessages((m) => [...m, r.data]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const saveEdit = async (m) => {
    const t = editText.trim();
    setEditingId(null);
    if (!t || t === m.content) return;
    try {
      const r = await api.patch(`/dms/messages/${m.id}`, { content: t });
      setMessages((ms) => ms.map((x) => (x.id === m.id ? r.data : x)));
    } catch (e) {
      toast.error("Couldn't edit message");
    }
  };
  const deleteMsg = (id) => {
    setMessages((ms) => ms.filter((x) => x.id !== id));
    api.delete(`/dms/messages/${id}`).catch(() => { toast.error("Couldn't delete"); load(); });
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#16181F]" data-testid="dm-view">
      <div className="h-16 shrink-0 border-b border-[#2E3242] bg-[#16181F]/80 backdrop-blur-md flex items-center gap-3 px-4">
        <button onClick={onBack} data-testid="dm-back" className="text-gray-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="relative">
          <img src={other?.avatar} alt={other?.name} className="w-9 h-9 rounded-full object-cover" />
          <Circle className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${other?.online ? "text-emerald-500 fill-emerald-500" : "text-gray-600 fill-gray-600"}`} />
        </div>
        <div className="min-w-0">
          <h1 className="font-display font-bold text-base leading-tight truncate">{other?.name || "Direct message"}</h1>
          <p className="text-xs text-gray-500">{other?.online ? "Online now" : `Last active ${relTime(other?.last_seen)}`}</p>
        </div>
        <button
          onClick={() => other && onCall?.(other)}
          data-testid="dm-call-button"
          className="ml-auto w-9 h-9 rounded-full flex items-center justify-center ruby-gradient text-white border border-red-900/40"
        >
          <Video className="w-4 h-4" />
        </button>
      </div>

      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 md:px-8 py-6" data-testid="dm-feed">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center text-gray-500">
            <p className="font-display font-bold text-lg text-gray-300">Say hi to {other?.name || ""}</p>
            <p className="text-sm mt-1">This conversation is just between the two of you.</p>
          </div>
        )}
        <div className="space-y-4 max-w-3xl mx-auto">
          {messages.map((m) => {
            const mine = m.author_id === user?.id;
            return (
              <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`flex gap-2.5 group ${mine ? "flex-row-reverse" : ""}`}>
                <img src={m.author_avatar} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                <div className={`min-w-0 max-w-[75%] ${mine ? "items-end" : ""} flex flex-col`}>
                  {editingId === m.id ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        data-testid={`dm-edit-input-${m.id}`}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(m); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                        className="bg-[#16181F] border-[#2E3242] focus-visible:ring-red-600 h-8 text-sm"
                      />
                      <button onClick={() => saveEdit(m)} className="text-emerald-400 hover:text-emerald-300"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingId(null)} className="text-gray-500 hover:text-gray-300"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    m.content && (
                      <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${mine ? "ruby-gradient text-white" : "bg-[#1E2029] text-gray-200 border border-[#2E3242]"}`}>
                        {m.content}
                      </div>
                    )
                  )}
                  <MessageAttachments attachments={m.attachments} />
                  <div className={`flex items-center gap-2 mt-1 px-1 ${mine ? "flex-row-reverse" : ""}`}>
                    <span className="text-[10px] text-gray-600">{relTime(m.created_at)}{m.edited ? " · edited" : ""}</span>
                    {mine && editingId !== m.id && (
                      <span className="flex items-center gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        {m.content && (
                          <button data-testid={`dm-edit-${m.id}`} onClick={() => { setEditingId(m.id); setEditText(m.content); }} className="text-gray-500 hover:text-blue-300">
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                        <button data-testid={`dm-delete-${m.id}`} onClick={() => deleteMsg(m.id)} className="text-gray-500 hover:text-red-400">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      <MessageComposer placeholder={`Message ${other?.name || ""}`} onSend={send} testIdPrefix="dm-" />
    </div>
  );
}
